import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Globe } from '@phosphor-icons/react';
import { useLanguages, flagFor } from '../context/LanguagesContext';

/**
 * Header language switcher. Languages list comes from the admin-managed
 * `platform_languages` collection (via LanguagesProvider) so adding /
 * removing a language in admin instantly reshapes this dropdown without
 * a code change.
 */
export const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const { items, loading } = useLanguages();

  // While the API loads we render with the fallback list (LanguagesContext
  // ships fr/en/mg as a default state) so the switcher is never empty.
  const languages = items;
  const current = languages.find((l) => l.code === i18n.language) || languages[0];

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    localStorage.setItem('language', langCode);
  };

  if (loading && !languages.length) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2" data-testid="language-switcher">
          <Globe size={20} />
          <span className="hidden sm:inline">{flagFor(current.code)} {current.code.toUpperCase()}</span>
          <span className="sm:hidden">{flagFor(current.code)}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => changeLanguage(lang.code)}
            className={i18n.language === lang.code ? 'bg-surface' : ''}
            data-testid={`language-${lang.code}`}
          >
            {flagFor(lang.code)} {lang.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
